<template>
    <ErrorSummary :response-status="responseStatus" />

    <div class="sm:mx-auto sm:w-full sm:max-w-md">
        <h1 class="mt-6 text-3xl font-bold tracking-tight text-center text-gray-900">What brings you to StageAgent?</h1>
        <h2 class="mt-2 text-lg text-center text-gray-600">Select all that apply</h2>
    </div>
    <SkeletonCard class="mt-4" v-if="isFetching" card-style="long" :num-of-cards=2 />

    <div v-else class="px-4 py-8 mt-8 sm:px-10">
        <div class="flex min-h-full">
            <div
                class="flex flex-col justify-center flex-1 pb-12 mx-auto space-y-4 lg:flex-none ">
                <div class="p-6 bg-white border rounded-3xl border-clay-500"
                    v-for="goalOption in goalsOptions" :key="goalOption.name">
                    <div class="relative flex items-start">
                        <div class="flex items-center h-8">
                            <input :id="goalOption.name" v-model="getGoalsForOnboardingResponse.theatreRoles"
                                :name="goalOption.name" :value="goalOption.value" aria-describedby=""
                                class="w-4 h-4 rounded text-electric-600 border-clay-300 focus:ring-electric-500"
                                type="checkbox">
                        </div>
                        <div class="ml-3 text-sm">
                            <label :for="goalOption.name"
                                class="mb-2 text-lg font-medium text-gray-700 cursor-pointer">{{
                                goalOption.description
                                }}
                                <span class="font-normal text-clay-800 cursor-pointer">({{goalOption.name}})</span>
                            </label>
                        </div>
                    </div>
                    <div v-if="getGoalsForOnboardingResponse.theatreRoles?.includes(goalOption.value)">
                        <dl v-if="goalOption.options.length > 0" class="mt-2 ml-6">
                            <dd>
                                <fieldset class="space-y-5">
                                    <legend class="sr-only">{{goalOption.name}}</legend>
                                    <div class="grid grid-cols-1 space-y-3 lg:grid-cols-3 lg:space-y-1">
                                        <div v-for="o in goalOption.options" class="relative flex items-start">
                                            <div class="flex items-center h-5">
                                                <input :id="o" v-model="goalOption.optionsSelected" :name="o" :value="o"
                                                    aria-describedby=""
                                                    class="w-4 h-4 border-2 border-gray-500 rounded text-clay-600 focus:ring-clay-500"
                                                    type="checkbox">
                                            </div>
                                            <div class="ml-3 text-sm">
                                                <label :for="o" class="font-medium text-gray-700">{{
                                                    o
                                                    }}</label> <span class="text-clay-800"></span>
                                            </div>
                                        </div>
                                    </div>
                                </fieldset>
                            </dd>
                        </dl>
                    </div>
                </div>

                <div class="py-3 mt-10 text-center">
                    <button
                        class="w-full inline-flex items-center justify-center px-4 py-[calc(theme(spacing.2)-1px)] rounded-full border border-transparent bg-electric-600 shadow-md whitespace-nowrap text-base font-medium text-white data-[disabled]:bg-clay-50 data-[hover]:bg-electric-500 data-[disabled]:text-clay-500"
                        type="submit" @click.prevent="submit">
                        Next
                    </button>

                    <button
                        class="justify-center hidden w-full px-4 py-2 mt-4 text-sm text-gray-600 underline hover:no-underline"
                        @click.prevent.stop="skip">
                        Skip
                    </button>
                </div>
                <button class="justify-center w-full px-4 py-2 mt-4 text-sm text-gray-600 underline hover:no-underline"
                    type="submit" @click.prevent="emit('onBack');">
                    Back
                </button>
            </div>

        </div>

    </div>

    <NotificationSuccess :show="updateSuccessful" content="Your changes were successfully saved."
        title="Successfully saved!" />
</template>

<script lang="ts" setup>
// @ts-ignore
import {defineEmits, onMounted, Ref, ref} from "vue";
import {client} from '@/shared';
import {
    GetGoalsForOnboarding, 
    GetGoalsForOnboardingResponse,
    UpdateGoalsForOnboarding
} from "@/dtos";

// @ts-ignore
import {ResponseStatus} from "@servicestack/client";
import ErrorSummary from "@/main/components/ErrorSummary.vue";
import NotificationSuccess from "@/main/components/NotificationSuccess.vue";
import SkeletonCard from "@/main/components/skeletons/SkeletonCard.vue";

const goalsOptions = ref([
    { name: "Performers", description: "I want to prepare for auditions and improve performance skills",  value: "Performer", options: [], optionsSelected: []},
    { name: "Producers", description: "I'm a producer or artistic staff looking to promote auditions, performances and/or classes", value: "Artistic Staff", options: [], optionsSelected: []},
    { name: "Teachers", description: "I'm a teacher and am looking for resources", value: "Teacher", options: [], optionsSelected: []},
    { name: "Student", description: "I’m a student looking for materials for a class", value: "Student", options: [], optionsSelected: []},
    { name: "Fan", description: "I'm a fan who wants to learn more about theatre", value: "Fan", options: [], optionsSelected: []}
]);

const emit = defineEmits(['onNext', 'onBack']);

const isFetching = ref(true);
onMounted(async () => {
    await client.get<GetGoalsForOnboardingResponse>(new GetGoalsForOnboarding())
        .then(response => {
            getGoalsForOnboardingResponse.value = response;
            goalsOptions.value[0].options = getGoalsForOnboardingResponse.value.performerRolesOptions;
            goalsOptions.value[0].optionsSelected = getGoalsForOnboardingResponse.value.performerRoles;
            
            goalsOptions.value[1].options = getGoalsForOnboardingResponse.value.staffRolesOptions;
            goalsOptions.value[1].optionsSelected = getGoalsForOnboardingResponse.value.staffRoles;
            
            goalsOptions.value[2].options = getGoalsForOnboardingResponse.value.teacherRolesOptions;
            goalsOptions.value[2].optionsSelected = getGoalsForOnboardingResponse.value.teacherRoles;

            isFetching.value = false;
        });
});

const responseStatus = ref(null);
const getGoalsForOnboardingResponse: Ref<GetGoalsForOnboardingResponse> = ref({});
const updateSuccessful: Ref<Boolean> = ref(false);

const submit = async () => {

    updateSuccessful.value = false;
    let request = getGoalsForOnboardingResponse.value;
    request.performerRoles = goalsOptions.value[0].optionsSelected;
    request.staffRoles = goalsOptions.value[1].optionsSelected;
    request.teacherRoles = goalsOptions.value[2].optionsSelected;

    await client.post<ResponseStatus>(new UpdateGoalsForOnboarding(request))
        .then(_ => {
            updateSuccessful.value = true;
            responseStatus.value = null;
            emit('onNext');
        })
        .catch(e => {
            responseStatus.value = e.responseStatus;
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        });
};

const skip = async () => emit('onNext');

</script>
<style>

</style>